import { type FC, useEffect, useState } from "react";
import {
  Box,
  AppBar,
  Card,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useProductsQuery } from "@/partner/entities/products";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { ProductsListItem } from "./ProductsListItem";
import { ProductsListToolbar } from "./ProductsListToolbar";
import { buildProductsFilter } from "../model/buildProductsFilter";

interface ProductsListProps {
  onEditProduct: (id: string) => void;
}

export const ProductsList: FC<ProductsListProps> = ({ onEditProduct }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter,
  } = useFilter();

  // référence: ProductsList.js useScrollToTop([listQuery.page,
  // listQuery.rowsPerPage]) — у нас нет аналога хука, скроллим вручную.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page, rowsPerPage]);

  const { data, loading } = useProductsQuery({
    variables: {
      page,
      rowsPerPage,
      ...buildProductsFilter(filter),
    },
    fetchPolicy: "cache-and-network",
    // référence: ProductsList.js onError — ошибка запроса списка снекбаром.
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const productsData = data?.products;
  const products = productsData?.nodes ?? [];
  const count = productsData?.count ?? 0;

  const [selectedItems, setSelectedItems] = useState<{ id: string }[]>([]);
  const selectedIds = selectedItems.map((item) => item.id);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(products.map((product) => ({ id: product.id })));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectOne = (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.stopPropagation();
    if (selectedIds.includes(id)) {
      setSelectedItems(selectedItems.filter((item) => item.id !== id));
    } else {
      setSelectedItems([...selectedItems, { id }]);
    }
  };

  const numSelected = selectedIds.length;
  const indeterminate = numSelected > 0 && numSelected < products.length;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (products.length === 0) {
    return <EmptyListCard title={t("products.list.empty")} />;
  }

  return (
    <Box>
      <Card elevation={0}>
        <AppBar position="static" color="default" elevation={0}>
          <ProductsListToolbar
            indeterminate={indeterminate}
            numSelected={numSelected}
            nodes={products}
            onSelectAllClick={handleSelectAll}
            selectedItems={selectedItems}
            onCloseSelected={() => setSelectedItems([])}
          />
        </AppBar>
      </Card>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        {products.map((product) => (
          <ProductsListItem
            key={product.id}
            product={product}
            selected={selectedIds.includes(product.id)}
            onSelectOneChange={handleSelectOne}
            onClickItem={onEditProduct}
          />
        ))}
      </Box>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Box>
  );
};
